import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { config } from "@/config";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────────────

export type EsOntologyLabel = "ESIndex" | "ESField" | "ESAlias";

export interface EsOntologyIndex {
  id?: string;
  name: string;
  shards?: string | null;
  replicas?: string | null;
  defaultAnalyzer?: string | null;
  aliasNames?: string[];
  fieldCount?: number;
  sourcePath?: string | null;
  dbOntologyId?: string;
  projectUuid?: string;
}

export interface EsOntologyField {
  id?: string;
  name: string;
  fullPath: string;
  parentPath?: string | null;
  indexId?: string;
  type: string;
  analyzer?: string | null;
  searchAnalyzer?: string | null;
  format?: string | null;
  copyTo?: string[];
  index?: boolean;
  docValues?: boolean;
  isNested?: boolean;
  isObject?: boolean;
  isMultiField?: boolean;
  ignoreAbove?: number | null;
}

export interface EsOntologyAlias {
  id?: string;
  name: string;
  indexId?: string;
  filter?: string | null;
  isWriteIndex?: boolean;
}

// ── Shared response normalization ──────────────────────────────────────
// The backend returns either a graph-node shape ({ id, label, properties })
// or a flat object, sometimes wrapped in { success, data, total }. These
// helpers flatten both shapes so consumers can rely on a single layout.

const unwrap = (payload: any): any => {
  if (!payload) return null;
  if (typeof payload === "object" && payload.data !== undefined && payload.success !== undefined) {
    return payload.data ?? payload;
  }
  return payload;
};

const flattenNode = (raw: any): any => {
  if (!raw || typeof raw !== "object") return raw;
  const props =
    raw.properties && typeof raw.properties === "object" ? raw.properties : null;
  return props ? { ...raw, ...props } : raw;
};

const normalizeListResponse = <T,>(
  payload: any,
  mapItem: (raw: any) => T,
): T[] => {
  const raw = unwrap(payload);
  if (!raw) return [];
  const candidates = [raw?.data, raw?.result, raw?.items, raw?.nodes, raw];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate.map(mapItem);
  }
  return [];
};

const normalizeIndex = (raw: any): EsOntologyIndex => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    shards: src?.shards ?? null,
    replicas: src?.replicas ?? null,
    defaultAnalyzer: src?.defaultAnalyzer ?? null,
    aliasNames: Array.isArray(src?.aliasNames) ? src.aliasNames : [],
    fieldCount: typeof src?.fieldCount === "number" ? src.fieldCount : undefined,
    sourcePath: src?.sourcePath ?? null,
    dbOntologyId: src?.dbOntologyId ?? undefined,
    projectUuid: src?.projectUuid ?? undefined,
  };
};

const normalizeField = (raw: any): EsOntologyField => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    fullPath: String(src?.fullPath ?? ""),
    parentPath: src?.parentPath ?? null,
    indexId: src?.indexId,
    type: String(src?.type ?? ""),
    analyzer: src?.analyzer ?? null,
    searchAnalyzer: src?.searchAnalyzer ?? null,
    format: src?.format ?? null,
    copyTo: Array.isArray(src?.copyTo) ? src.copyTo : [],
    index: src?.index !== false,
    docValues: src?.docValues !== false,
    isNested: !!src?.isNested,
    isObject: !!src?.isObject,
    isMultiField: !!src?.isMultiField,
    ignoreAbove:
      typeof src?.ignoreAbove === "number" ? src.ignoreAbove : null,
  };
};

const normalizeAlias = (raw: any): EsOntologyAlias => {
  const src = flattenNode(raw);
  return {
    ...(src?.id ? { id: String(src.id) } : {}),
    name: String(src?.name ?? ""),
    indexId: src?.indexId,
    filter: src?.filter ?? null,
    isWriteIndex: !!src?.isWriteIndex,
  };
};

const parseJson = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const throwOnError = async (
  response: Response,
  fallback: string,
): Promise<void> => {
  if (response.ok) return;
  let errorMessage = `${fallback}: ${response.statusText}`;
  try {
    const errJson = await response.json();
    if (errJson?.message) errorMessage = errJson.message;
  } catch {
    /* swallow */
  }
  throw new Error(errorMessage);
};

// ── Queries ────────────────────────────────────────────────────────────

interface ListOptions {
  // Required by the API: the caller must be a member of this project and the
  // data lake must belong to it. The query stays disabled until it is set.
  projectUuid?: string;
  // Optional filter narrowing — passed as filters[<field>][$eq]=value.
  filters?: Record<string, string | number | boolean>;
  limit?: number;
  page?: number;
}

function buildListUrl(
  dataLakeId: string,
  label: EsOntologyLabel,
  options: ListOptions = {},
) {
  const params = new URLSearchParams();
  for (const [field, value] of Object.entries(options.filters ?? {})) {
    params.set(`filters[${field}][$eq]`, String(value));
  }
  if (options.projectUuid) params.set("projectUuid", options.projectUuid);
  if (options.limit !== undefined) params.set("limit", String(options.limit));
  if (options.page !== undefined) params.set("page", String(options.page));
  const qs = params.toString();
  return `${config.isometricApiUrl}/db-ontology/${encodeURIComponent(
    dataLakeId,
  )}/${label}${qs ? `?${qs}` : ""}`;
}

export function useGetEsOntologyIndexes(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: ListOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  return useQuery({
    queryKey: [
      "db-ontology",
      "ESIndex",
      dataLakeId,
      options.limit ?? null,
      options.page ?? null,
    ],
    queryFn: async (): Promise<EsOntologyIndex[]> => {
      const response = await apiFetch(buildListUrl(dataLakeId!, "ESIndex", options), {
        method: "GET",
        headers: getAuthHeaders(),
      });
      await throwOnError(response, "Failed to fetch ES indexes");
      return normalizeListResponse(await parseJson(response), normalizeIndex);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface GetEsFieldsOptions extends ListOptions {
  indexId?: string;
}

export function useGetEsOntologyFields(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetEsFieldsOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { indexId, ...listOptions } = options;
  const mergedFilters = { ...(listOptions.filters ?? {}), ...(indexId ? { indexId } : {}) };
  return useQuery({
    queryKey: [
      "db-ontology",
      "ESField",
      dataLakeId,
      indexId ?? null,
      listOptions.limit ?? null,
      listOptions.page ?? null,
    ],
    queryFn: async (): Promise<EsOntologyField[]> => {
      const response = await apiFetch(
        buildListUrl(dataLakeId!, "ESField", { ...listOptions, filters: mergedFilters }),
        { method: "GET", headers: getAuthHeaders() },
      );
      await throwOnError(response, "Failed to fetch ES fields");
      return normalizeListResponse(await parseJson(response), normalizeField);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

export interface GetEsAliasesOptions extends ListOptions {
  indexId?: string;
}

export function useGetEsOntologyAliases(
  dataLakeId: string | undefined,
  enabled: boolean = true,
  options: GetEsAliasesOptions = {},
) {
  const { getAuthHeaders } = useAuthHeaders();
  const { indexId, ...listOptions } = options;
  const mergedFilters = { ...(listOptions.filters ?? {}), ...(indexId ? { indexId } : {}) };
  return useQuery({
    queryKey: [
      "db-ontology",
      "ESAlias",
      dataLakeId,
      indexId ?? null,
      listOptions.limit ?? null,
      listOptions.page ?? null,
    ],
    queryFn: async (): Promise<EsOntologyAlias[]> => {
      const response = await apiFetch(
        buildListUrl(dataLakeId!, "ESAlias", { ...listOptions, filters: mergedFilters }),
        { method: "GET", headers: getAuthHeaders() },
      );
      await throwOnError(response, "Failed to fetch ES aliases");
      return normalizeListResponse(await parseJson(response), normalizeAlias);
    },
    enabled: enabled && !!dataLakeId && !!options.projectUuid,
    staleTime: 60000,
    retry: 1,
  });
}

// ── Mutations: ESIndex ────────────────────────────────────────────────

export interface CreateEsIndexPayload {
  projectUuid: string;
  dataLakeId: string;
  name: string;
  shards?: string;
  replicas?: string;
  defaultAnalyzer?: string;
  sourcePath?: string;
}

export function useCreateEsIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEsIndexPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-index`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      await throwOnError(response, "Failed to create ES index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESIndex", variables.dataLakeId] });
      toast.success("ES index created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create ES index");
    },
  });
}

export interface UpdateEsIndexPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
  data: Partial<{
    name: string;
    shards: string;
    replicas: string;
    defaultAnalyzer: string;
    sourcePath: string;
  }>;
}

export function useUpdateEsIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateEsIndexPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-index/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update ES index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "ESIndex", variables.dataLakeId],
        });
      }
      toast.success("ES index updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update ES index");
    },
  });
}

export interface DeleteEsIndexPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
}

export function useDeleteEsIndex() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteEsIndexPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-index/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete ES index");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        // Deleting an index cascades to fields and aliases.
        for (const label of ["ESIndex", "ESField", "ESAlias"] as const) {
          queryClient.invalidateQueries({
            queryKey: ["db-ontology", label, variables.dataLakeId],
          });
        }
      }
      toast.success("ES index deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete ES index");
    },
  });
}

// ── Mutations: ESField ────────────────────────────────────────────────

export interface CreateEsFieldPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  indexId: string;
  dataLakeId?: string; // for invalidation
  data: {
    name: string;
    fullPath: string;
    parentPath?: string;
    type: string;
    analyzer?: string;
    searchAnalyzer?: string;
    format?: string;
    copyTo?: string[];
    index?: boolean;
    docValues?: boolean;
    isNested?: boolean;
    isObject?: boolean;
    isMultiField?: boolean;
    ignoreAbove?: number;
  };
}

export function useCreateEsField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEsFieldPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-index/${encodeURIComponent(payload.indexId)}/field?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data),
      });
      await throwOnError(response, "Failed to create ES field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESField", variables.dataLakeId] });
        // fieldCount on parent ESIndex changes — refetch the index list too.
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESIndex", variables.dataLakeId] });
      }
      toast.success("ES field added");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create ES field");
    },
  });
}

export interface UpdateEsFieldPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
  data: Partial<{
    type: string;
    analyzer: string;
    searchAnalyzer: string;
    format: string;
    copyTo: string[];
    index: boolean;
    docValues: boolean;
    ignoreAbove: number;
  }>;
}

export function useUpdateEsField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateEsFieldPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-field/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update ES field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "ESField", variables.dataLakeId],
        });
      }
      toast.success("ES field updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update ES field");
    },
  });
}

export interface DeleteEsFieldPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
}

export function useDeleteEsField() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteEsFieldPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-field/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete ES field");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESField", variables.dataLakeId] });
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESIndex", variables.dataLakeId] });
      }
      toast.success("ES field deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete ES field");
    },
  });
}

// ── Mutations: ESAlias ────────────────────────────────────────────────

export interface CreateEsAliasPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  indexId: string;
  dataLakeId?: string;
  data: {
    name: string;
    filter?: string;
    isWriteIndex?: boolean;
  };
}

export function useCreateEsAlias() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEsAliasPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-index/${encodeURIComponent(payload.indexId)}/alias?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data),
      });
      await throwOnError(response, "Failed to create ES alias");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESAlias", variables.dataLakeId] });
        // aliasNames on parent ESIndex changes — refresh the index list too.
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESIndex", variables.dataLakeId] });
      }
      toast.success("ES alias added");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create ES alias");
    },
  });
}

export interface UpdateEsAliasPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
  data: Partial<{
    filter: string;
    isWriteIndex: boolean;
  }>;
}

export function useUpdateEsAlias() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateEsAliasPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-alias/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload.data ?? {}),
      });
      await throwOnError(response, "Failed to update ES alias");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({
          queryKey: ["db-ontology", "ESAlias", variables.dataLakeId],
        });
      }
      toast.success("ES alias updated");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update ES alias");
    },
  });
}

export interface DeleteEsAliasPayload {
  /** Required by the API on every project-scoped write; the caller's role is checked against it. */
  projectUuid: string;
  id: string;
  dataLakeId?: string;
}

export function useDeleteEsAlias() {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeleteEsAliasPayload) => {
      const url = `${config.isometricApiUrl}/db-ontology/es-alias/${encodeURIComponent(payload.id)}?projectUuid=${encodeURIComponent(payload.projectUuid)}`;
      const response = await apiFetch(url, { method: "DELETE", headers: getAuthHeaders() });
      await throwOnError(response, "Failed to delete ES alias");
      return parseJson(response);
    },
    onSuccess: (_, variables) => {
      if (variables.dataLakeId) {
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESAlias", variables.dataLakeId] });
        queryClient.invalidateQueries({ queryKey: ["db-ontology", "ESIndex", variables.dataLakeId] });
      }
      toast.success("ES alias deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete ES alias");
    },
  });
}
